import {
  Building2,
  Check,
  ChevronDown,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getCustomers } from '@/modules/customer/api/customer.api'
import { toast } from '@/shared/lib/toast'

const getTodayDateString = () => new Date().toISOString().split('T')[0]

const EMAIL_VALIDATION_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i

const createEmptyCall = (index = 1) => ({
  id: `${Date.now()}-${Math.random()}`,
  callNumber: index,
  selectedCustomerId: null,
  customerName: '',
  contactPersonName: '',
  phoneNumber: '',
  email: '',
  remark: '',
})

export default function AddDCRModal({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}) {
  const todayDate = useMemo(() => getTodayDateString(), [])
  const [calls, setCalls] = useState([])
  const [customers, setCustomers] = useState([])
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false)
  const [activeDropdownCallId, setActiveDropdownCallId] = useState(null)
  const [customerSearch, setCustomerSearch] = useState('')

  const dropdownContainerRef = useRef(null)

  const [prevOpen, setPrevOpen] = useState(open)

  if (open !== prevOpen) {
    setPrevOpen(open)

    if (!open) {
      setCalls([])
      setActiveDropdownCallId(null)
      setCustomerSearch('')
    }
  }

  // Fetch customers when modal opens
  useEffect(() => {
    if (!open) return

    let isMounted = true

    const loadCustomers = async () => {
      try {
        setIsLoadingCustomers(true)
        const res = await getCustomers({ limit: 100 })
        if (!isMounted) return

        const list = res.data?.customers || res.customers || []
        setCustomers(list)
      } catch (err) {
        console.error('Failed to fetch customers for DCR dropdown:', err)
      } finally {
        if (isMounted) {
          setIsLoadingCustomers(false)
        }
      }
    }

    loadCustomers()

    return () => {
      isMounted = false
    }
  }, [open])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownContainerRef.current &&
        !dropdownContainerRef.current.contains(event.target)
      ) {
        setActiveDropdownCallId(null)
      }
    }

    if (activeDropdownCallId) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeDropdownCallId])

  // Filter customers by search term
  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase()
    if (!q) return customers

    return customers.filter(
      (c) =>
        (c.companyName || '').toLowerCase().includes(q) ||
        (c.contactPersonName || '').toLowerCase().includes(q) ||
        (c.contactPersonPhone || '').toLowerCase().includes(q) ||
        (c.contactPersonEmail || '').toLowerCase().includes(q),
    )
  }, [customers, customerSearch])

  // Add Call button handler
  const handleAddCall = () => {
    const newIndex = calls.length + 1
    const newCall = createEmptyCall(newIndex)
    setCalls((prev) => [...prev, newCall])
    setActiveDropdownCallId(newCall.id)
    setCustomerSearch('')
  }

  // Remove Call handler
  const handleRemoveCall = (callId) => {
    setCalls((prev) =>
      prev
        .filter((c) => c.id !== callId)
        .map((c, idx) => ({
          ...c,
          callNumber: idx + 1,
        })),
    )

    if (activeDropdownCallId === callId) {
      setActiveDropdownCallId(null)
    }
  }

  // Toggle Dropdown for a specific call
  const handleToggleDropdown = (callId) => {
    setActiveDropdownCallId((prev) => (prev === callId ? null : callId))
    setCustomerSearch('')
  }

  // Select customer for a call
  const handleSelectCustomer = (callId, customer) => {
    setCalls((prev) =>
      prev.map((call) =>
        call.id === callId
          ? {
              ...call,
              selectedCustomerId: customer._id,
              customerName: customer.companyName || '',
              contactPersonName: customer.contactPersonName || '',
              phoneNumber: customer.contactPersonPhone || '',
              email: customer.contactPersonEmail || '',
            }
          : call,
      ),
    )

    setActiveDropdownCallId(null)
    setCustomerSearch('')
  }

  // Clear customer selection for a call
  const handleClearCustomer = (callId) => {
    setCalls((prev) =>
      prev.map((call) =>
        call.id === callId
          ? {
              ...call,
              selectedCustomerId: null,
              customerName: '',
              contactPersonName: '',
              phoneNumber: '',
              email: '',
            }
          : call,
      ),
    )
  }

  // Field change handler for a call
  const handleCallFieldChange = (callId, field, value) => {
    let sanitized = value
    if (field === 'customerName') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.,&'()/-]/g, '')
    } else if (field === 'contactPersonName') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.'-]/g, '')
    } else if (field === 'phoneNumber') {
      sanitized = value.replace(/\D/g, '').slice(0, 10)
    }

    setCalls((prev) =>
      prev.map((call) =>
        call.id === callId
          ? {
              ...call,
              [field]: sanitized,
            }
          : call,
      ),
    )
  }

  const handleClose = () => {
    if (isSubmitting) return

    setCalls([])
    setActiveDropdownCallId(null)
    setCustomerSearch('')

    onOpenChange(false)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (calls.length === 0) {
      toast.error(
        'Please click "+ Add Call" to record at least one customer visit.',
      )
      return
    }

    // Validate all calls
    for (let i = 0; i < calls.length; i++) {
      const call = calls[i]
      const callLabel = `Call ${i + 1}`

      if (!call.customerName?.trim() || call.customerName.trim().length < 2) {
        toast.error(`${callLabel}: Company Name is required (min 2 characters)`)
        return
      }

      if (
        !call.contactPersonName?.trim() ||
        call.contactPersonName.trim().length < 2
      ) {
        toast.error(
          `${callLabel}: Contact Person Name is required (min 2 characters)`,
        )
        return
      }

      if (!call.phoneNumber?.trim() || call.phoneNumber.trim().length !== 10) {
        toast.error(
          `${callLabel}: Phone Number must be numeric and exactly 10 digits`,
        )
        return
      }

      if (
        !call.email?.trim() ||
        !EMAIL_VALIDATION_REGEX.test(call.email.trim())
      ) {
        toast.error(`${callLabel}: Please enter a valid email address`)
        return
      }
    }

    try {
      const first = calls[0]
      const payload = {
        date: todayDate,
        customerName: first.customerName.trim(),
        contactPersonName: first.contactPersonName.trim(),
        phoneNumber: first.phoneNumber.trim(),
        email: first.email.trim(),
        calls: calls.map((c, idx) => ({
          callNumber: idx + 1,
          customerName: c.customerName.trim(),
          contactPersonName: c.contactPersonName.trim(),
          phoneNumber: c.phoneNumber.trim(),
          email: c.email.trim(),
          remark: c.remark?.trim() || '',
        })),
      }
      await onSubmit(payload)

      setCalls([])
      setActiveDropdownCallId(null)
      setCustomerSearch('')
      onOpenChange(false)
    } catch {
      // Error handled by store
    }
  }

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
        <div
          ref={dropdownContainerRef}
          className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:rounded-3xl sm:p-7"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Add DCR</h2>
              <p className="mt-1 text-sm text-slate-500">
                Record customer visits for today.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="rounded-full p-2 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Top Bar: Date Input (non-editable, current date only) + Add Call Button to the right */}
            <div className="mt-6 flex items-end gap-3 sm:gap-4">
              <div className="flex-1">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Date
                  <span className="ml-1.5 text-xs font-normal text-slate-400">
                    (Current date • Non-editable)
                  </span>
                </label>

                <input
                  type="date"
                  value={todayDate}
                  readOnly
                  disabled
                  min={todayDate}
                  max={todayDate}
                  className="w-full rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 outline-none cursor-not-allowed select-none"
                />
              </div>

              {/* Add Call Button directly to the right side of Date */}
              <button
                type="button"
                onClick={handleAddCall}
                disabled={isSubmitting}
                className="inline-flex h-[46px] shrink-0 items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                <span>Add Call</span>
              </button>
            </div>

            {/* Empty State when no calls added yet */}
            {calls.length === 0 && (
              <div className="mt-6 rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center bg-slate-50/50">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  <Building2 className="h-6 w-6" />
                </div>
                <p className="mt-3 text-sm font-semibold text-slate-700">
                  No calls added yet
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Click the &quot;+ Add Call&quot; button above to select a
                  customer and record details.
                </p>
              </div>
            )}

            {/* List of Calls */}
            {calls.length > 0 && (
              <div className="mt-6 space-y-6">
                {calls.map((call, index) => {
                  const isDropdownOpen = activeDropdownCallId === call.id
                  const selectedCustomer = customers.find(
                    (c) => c._id === call.selectedCustomerId,
                  )

                  return (
                    <div
                      key={call.id}
                      className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5"
                    >
                      {/* Section Header: Call X + Trash */}
                      <div className="mb-4 flex items-center justify-between border-b border-slate-200/80 pb-3">
                        <div className="flex items-center gap-2">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">
                            {index + 1}
                          </span>
                          <span className="text-sm font-bold text-slate-900">
                            Call {index + 1}
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveCall(call.id)}
                          disabled={isSubmitting}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                          title={`Remove Call ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Customer Selection Dropdown */}
                      <div className="relative mb-4">
                        <div className="mb-1.5 flex items-center justify-between">
                          <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
                            <Building2 className="h-3.5 w-3.5 text-slate-500" />
                            <span>
                              Select Customer (From Customers Section)
                            </span>
                          </label>

                          {call.selectedCustomerId && (
                            <button
                              type="button"
                              onClick={() => handleClearCustomer(call.id)}
                              className="text-xs font-medium text-slate-500 hover:text-slate-900 hover:underline"
                            >
                              Clear / Enter Manually
                            </button>
                          )}
                        </div>

                        {/* Trigger button */}
                        <button
                          type="button"
                          onClick={() => handleToggleDropdown(call.id)}
                          className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm transition-colors hover:border-slate-300 focus:border-slate-400 focus:outline-none"
                        >
                          {selectedCustomer ? (
                            <div className="flex items-center gap-2 overflow-hidden">
                              <span className="truncate font-semibold text-slate-900">
                                {selectedCustomer.companyName}
                              </span>
                              <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                                Auto-filled
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400">
                              {isLoadingCustomers
                                ? 'Loading customers...'
                                : 'Choose an existing customer to auto-fill details...'}
                            </span>
                          )}

                          <div className="flex items-center gap-1.5 text-slate-400">
                            {isLoadingCustomers ? (
                              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                            ) : (
                              <ChevronDown
                                className={`h-4 w-4 transition-transform duration-200 ${
                                  isDropdownOpen ? 'rotate-180' : ''
                                }`}
                              />
                            )}
                          </div>
                        </button>

                        {/* Dropdown Menu */}
                        {isDropdownOpen && (
                          <div className="absolute left-0 right-0 z-30 mt-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                            {/* Search inside dropdown */}
                            <div className="relative mb-2">
                              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <input
                                type="text"
                                value={customerSearch}
                                onChange={(e) =>
                                  setCustomerSearch(e.target.value)
                                }
                                placeholder="Search customer by name, contact, phone..."
                                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-2 pl-9 pr-8 text-sm outline-none focus:border-slate-400 focus:bg-white"
                                autoFocus
                              />
                              {customerSearch && (
                                <button
                                  type="button"
                                  onClick={() => setCustomerSearch('')}
                                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>

                            {/* Customers List */}
                            <div className="max-h-60 space-y-1 overflow-y-auto">
                              {filteredCustomers.length === 0 ? (
                                <div className="py-6 text-center text-sm text-slate-400">
                                  {customers.length === 0
                                    ? 'No customers found in Customers section'
                                    : `No customer matching "${customerSearch}"`}
                                </div>
                              ) : (
                                filteredCustomers.map((customer) => {
                                  const isSelected =
                                    call.selectedCustomerId === customer._id

                                  return (
                                    <button
                                      key={customer._id}
                                      type="button"
                                      onClick={() =>
                                        handleSelectCustomer(call.id, customer)
                                      }
                                      className={`flex w-full items-start justify-between rounded-xl px-3 py-2.5 text-left transition-colors ${
                                        isSelected
                                          ? 'bg-indigo-50/70 text-indigo-950'
                                          : 'text-slate-800 hover:bg-slate-50'
                                      }`}
                                    >
                                      <div className="flex min-w-0 items-start gap-2.5 pr-2">
                                        <Building2
                                          className={`mt-0.5 h-4 w-4 shrink-0 ${
                                            isSelected
                                              ? 'text-indigo-600'
                                              : 'text-slate-400'
                                          }`}
                                        />
                                        <div className="min-w-0">
                                          <p className="truncate text-sm font-semibold">
                                            {customer.companyName}
                                          </p>
                                          <p className="mt-0.5 truncate text-xs text-slate-500">
                                            {customer.contactPersonName}
                                            {customer.contactPersonPhone &&
                                              ` • ${customer.contactPersonPhone}`}
                                            {customer.contactPersonEmail &&
                                              ` • ${customer.contactPersonEmail}`}
                                          </p>
                                        </div>
                                      </div>

                                      {isSelected && (
                                        <Check className="mt-1 h-4 w-4 shrink-0 text-indigo-600" />
                                      )}
                                    </button>
                                  )
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Call Fields */}
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {/* Company Name */}
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                            Company Name
                            {call.selectedCustomerId && (
                              <span className="ml-1.5 text-[11px] font-normal text-slate-400">
                                (Auto-fetched)
                              </span>
                            )}
                          </label>

                          <input
                            type="text"
                            value={call.customerName}
                            onChange={(e) =>
                              handleCallFieldChange(
                                call.id,
                                'customerName',
                                e.target.value,
                              )
                            }
                            placeholder="Enter customer name"
                            required
                            readOnly={Boolean(call.selectedCustomerId)}
                            className={`w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition-colors ${
                              call.selectedCustomerId
                                ? 'bg-slate-100 text-slate-600 cursor-not-allowed select-none'
                                : 'bg-white focus:border-slate-400'
                            }`}
                          />
                        </div>

                        {/* Contact Person Name */}
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                            Contact Person Name
                            {call.selectedCustomerId && (
                              <span className="ml-1.5 text-[11px] font-normal text-slate-400">
                                (Auto-fetched)
                              </span>
                            )}
                          </label>

                          <input
                            type="text"
                            value={call.contactPersonName}
                            onChange={(e) =>
                              handleCallFieldChange(
                                call.id,
                                'contactPersonName',
                                e.target.value,
                              )
                            }
                            placeholder="Enter contact person name"
                            required
                            readOnly={Boolean(call.selectedCustomerId)}
                            className={`w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition-colors ${
                              call.selectedCustomerId
                                ? 'bg-slate-100 text-slate-600 cursor-not-allowed select-none'
                                : 'bg-white focus:border-slate-400'
                            }`}
                          />
                        </div>

                        {/* Phone Number */}
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                            Phone Number
                            {call.selectedCustomerId && (
                              <span className="ml-1.5 text-[11px] font-normal text-slate-400">
                                (Auto-fetched)
                              </span>
                            )}
                          </label>

                          <input
                            type="tel"
                            maxLength={10}
                            value={call.phoneNumber}
                            onChange={(e) =>
                              handleCallFieldChange(
                                call.id,
                                'phoneNumber',
                                e.target.value,
                              )
                            }
                            placeholder="Enter 10-digit phone number"
                            required
                            readOnly={Boolean(call.selectedCustomerId)}
                            className={`w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition-colors ${
                              call.selectedCustomerId
                                ? 'bg-slate-100 text-slate-600 cursor-not-allowed select-none'
                                : 'bg-white focus:border-slate-400'
                            }`}
                          />
                        </div>

                        {/* Email */}
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                            Email
                            {call.selectedCustomerId && (
                              <span className="ml-1.5 text-[11px] font-normal text-slate-400">
                                (Auto-fetched)
                              </span>
                            )}
                          </label>

                          <input
                            type="email"
                            value={call.email}
                            onChange={(e) =>
                              handleCallFieldChange(
                                call.id,
                                'email',
                                e.target.value,
                              )
                            }
                            placeholder="Enter email address"
                            required
                            readOnly={Boolean(call.selectedCustomerId)}
                            className={`w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition-colors ${
                              call.selectedCustomerId
                                ? 'bg-slate-100 text-slate-600 cursor-not-allowed select-none'
                                : 'bg-white focus:border-slate-400'
                            }`}
                          />
                        </div>

                        {/* Remark */}
                        <div className="md:col-span-2">
                          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                            Remark
                          </label>

                          <input
                            type="text"
                            value={call.remark}
                            onChange={(e) =>
                              handleCallFieldChange(
                                call.id,
                                'remark',
                                e.target.value,
                              )
                            }
                            placeholder={`Enter remark for Call ${index + 1}`}
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400"
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Footer */}
            <div className="mt-7 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end sm:gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="w-full rounded-full border border-slate-200 px-6 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting || calls.length === 0}
                className="w-full rounded-full bg-slate-950 px-7 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {isSubmitting ? 'Adding...' : 'Add DCR'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
